import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Truck, Ban, Clock, Mail, Loader2 } from "lucide-react";
import { AdminGate, ADMIN_KEY, AdminSwitch } from "@/components/AdminGate";

const STATUS = {
  pending: { label: "Pending", icon: Clock, cls: "bg-gold-light text-ink" },
  shipped: { label: "Shipped", icon: Truck, cls: "bg-olive/15 text-olive" },
  cannot_ship: { label: "Can't ship", icon: Ban, cls: "bg-red-100 text-red-800" },
};
const FILTERS = [["", "All"], ["pending", "Pending"], ["shipped", "Shipped"], ["cannot_ship", "Can't ship"]];

function ShippedEmail({ r, adminKey, onSaved }) {
  const [mailing, setMailing] = useState(false);
  if (!r.email) {
    return <p data-testid={`request-no-email-${r.id}`} className="mt-4 text-xs text-ink/50">No email address left — can't send a shipped note.</p>;
  }
  const send = async () => {
    if (r.shipped_email_sent_at && !window.confirm("A shipped email was already sent. Send it again?")) return;
    setMailing(true);
    try {
      const { data } = await api.post(`/admin/bible-requests/${r.id}/send-shipped`, null, { headers: { "X-Admin-Key": adminKey } });
      toast.success(`Shipped email sent to ${r.email}`);
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send the email");
    } finally { setMailing(false); }
  };
  return (
    <div className="mt-4">
      <Button data-testid={`request-send-shipped-${r.id}`} variant="outline" disabled={mailing} onClick={send} className="rounded-full">
        {mailing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
        {r.shipped_email_sent_at ? "Send shipped email again" : "Send shipped email"}
      </Button>
      {r.shipped_email_sent_at && (
        <p data-testid={`request-shipped-email-sent-${r.id}`} className="text-xs text-olive mt-2">Shipped email sent {new Date(r.shipped_email_sent_at).toLocaleString()}</p>
      )}
    </div>
  );
}

function RequestCard({ r, adminKey, onSaved }) {
  const [f, setF] = useState({ status: r.status, tracking_number: r.tracking_number, shipped_date: r.shipped_date, admin_note: r.admin_note });
  const [busy, setBusy] = useState(false);
  const S = STATUS[r.status] || STATUS.pending;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/bible-requests/${r.id}`, f, { headers: { "X-Admin-Key": adminKey } });
      toast.success("Request updated");
      onSaved(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally { setBusy(false); }
  };
  return (
    <div data-testid={`request-card-${r.id}`} className="bg-card border border-border rounded-2xl p-6 grid md:grid-cols-[1fr_320px] gap-6">
      <div>
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-serif text-2xl font-600 text-ink">{r.name}</h3>
          <span data-testid={`request-status-${r.id}`} className={`inline-flex items-center gap-1 text-xs font-700 rounded-full px-3 py-1 ${S.cls}`}>
            <S.icon className="w-3.5 h-3.5" /> {S.label}
          </span>
        </div>
        <p className="text-xs font-mono text-ink/45 mt-1">Requested {new Date(r.created_at).toLocaleDateString()}</p>
        <p className="mt-3 text-ink/80 whitespace-pre-line leading-relaxed">{r.address}</p>
        <div className="mt-3 text-sm text-ink/65 space-y-1">
          {r.email && <p>Email: <a className="text-olive hover:text-gold" href={`mailto:${r.email}`}>{r.email}</a></p>}
          <p>Translation: {r.translation}</p>
          {r.note && <p>Note: {r.note}</p>}
          {r.status === "shipped" && <p>Shipped {r.shipped_date}{r.tracking_number && ` · Tracking ${r.tracking_number}`}</p>}
        </div>
        {r.status === "shipped" && <ShippedEmail r={r} adminKey={adminKey} onSaved={onSaved} />}
      </div>
      <div className="space-y-2.5">
        <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
          <SelectTrigger data-testid={`request-status-select-${r.id}`} className="bg-parchment h-10"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid={`request-tracking-${r.id}`} placeholder="Tracking number (optional)" value={f.tracking_number}
          onChange={(e) => setF({ ...f, tracking_number: e.target.value })} className="bg-parchment" />
        <Input data-testid={`request-date-${r.id}`} type="date" value={f.shipped_date}
          onChange={(e) => setF({ ...f, shipped_date: e.target.value })} className="bg-parchment" />
        <Input data-testid={`request-note-${r.id}`} placeholder="Private note (optional)" value={f.admin_note}
          onChange={(e) => setF({ ...f, admin_note: e.target.value })} className="bg-parchment" />
        <Button data-testid={`request-save-${r.id}`} disabled={busy} onClick={save}
          className="w-full rounded-full bg-gold text-ink hover:bg-ink hover:text-parchment font-700">Save</Button>
      </div>
    </div>
  );
}

export default function RequestTracker() {
  const [adminKey, setAdminKey] = useState(sessionStorage.getItem(ADMIN_KEY) || "");
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");

  useEffect(() => {
    if (!adminKey) return;
    api.get("/admin/bible-requests", { params: { status: filter }, headers: { "X-Admin-Key": adminKey } })
      .then(({ data }) => setItems(data))
      .catch(() => { sessionStorage.removeItem(ADMIN_KEY); setAdminKey(""); });
  }, [adminKey, filter]);

  const onSaved = (d) => setItems((xs) => (filter && d.status !== filter ? xs.filter((x) => x.id !== d.id) : xs.map((x) => (x.id === d.id ? d : x))));

  return (
    <div data-testid="request-tracker-page" className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 pb-16">
      {adminKey && <AdminSwitch />}
      <h1 className="font-serif text-4xl sm:text-5xl font-700 text-ink mb-8 flex items-center gap-3"><Package className="w-9 h-9 text-gold" /> Free Bible Requests</h1>
      {!adminKey ? <AdminGate onUnlock={setAdminKey} /> : (
        <>
          <div className="flex flex-wrap gap-2 mb-6">
            {FILTERS.map(([k, label]) => (
              <button key={k || "all"} data-testid={`request-filter-${k || "all"}`} onClick={() => setFilter(k)}
                className={`px-4 py-1.5 rounded-full text-sm border transition-colors ${filter === k ? "bg-ink text-parchment border-ink" : "bg-card border-border text-ink/70 hover:border-gold"}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="space-y-4" data-testid="request-list">
            {items.length === 0 && <p data-testid="request-list-empty" className="text-ink/55">No requests here.</p>}
            {items.map((r) => <RequestCard key={`${r.id}-${r.status}`} r={r} adminKey={adminKey} onSaved={onSaved} />)}
          </div>
        </>
      )}
    </div>
  );
}
